import { SyntheticEvent, useState } from "react";
import { useTranslator } from "@eo-locale/react";

import {
  Box,
  Button,
  Alert,
  AlertIcon,
  CloseButton,
  FormControl,
  FormLabel,
  HStack,
  PinInput,
  PinInputField,
  HTMLWrapper
} from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import { saveUserConfirmation } from "@/redux/features/user-confirmation/thunk";
import { resetOtpError } from "@/redux/features/user-confirmation/slice";

const OTP_LENGTH = 4;

const VerificationForm = (): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const [otp, setOtp] = useState<string>("");
  const { registrations } = useAppSelector((state) => state.registrations);
  const { status, error } = useAppSelector((state) => state.userConfirmation);

  const handleSubmit = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    dispatch(saveUserConfirmation(otp));
  };

  return (
    <Box as="form" my={2} noValidate onSubmit={handleSubmit}>
      <HTMLWrapper
        data-testid="accountVerification-yourEmailText"
        html={translate("register.verifyCodeHelp", {
          length: OTP_LENGTH,
          email: registrations?.email || translate("register.yourEmail")
        })}
      />
      <FormControl
        my={4}
        isRequired
        id="otp"
        isDisabled={status === "saving"}
        isInvalid={error?.length > 0}
      >
        <FormLabel textAlign="center" fontWeight="semibold">
          {translate("register.enterVerificationCode")}
        </FormLabel>
        <HStack justifyContent="center">
          <PinInput
            otp
            autoFocus
            onChange={setOtp}
            onComplete={setOtp}
            isDisabled={status === "saving"}
            isInvalid={error?.length > 0}
          >
            <PinInputField data-testid="accountVerification-pinInput-1" />
            <PinInputField data-testid="accountVerification-pinInput-2" />
            <PinInputField data-testid="accountVerification-pinInput-3" />
            <PinInputField data-testid="accountVerification-pinInput-4" />
          </PinInput>
        </HStack>
        {error && (
          <Alert status="error" my={5}>
            <AlertIcon />
            {translate("register.pinError")}
            <CloseButton ml="auto" onClick={() => dispatch(resetOtpError())} />
          </Alert>
        )}
      </FormControl>

      <Button
        variant="primary"
        type="submit"
        width="full"
        isLoading={status === "saving"}
        isDisabled={otp.length !== OTP_LENGTH}
        data-testid="accountVerification-confirmAccountButton"
      >
        {translate("register.confirmAccount")}
      </Button>
    </Box>
  );
};

export default VerificationForm;
